import { useEffect, useState } from "react";
import { BrowserRouter, Route, Switch } from "react-router-dom";
import { sum } from "@jurijtokarski/calc";

import { RouteKey, RoutePath } from "./configs/routes";
import { Home } from "./pages/Home";
import { History } from "./pages/History";
import { SetGoal } from "./pages/SetGoal";
import { AddDrink } from "./pages/AddDrink";

import { Context, AppContext, AppContextValues, DEFAULT_CONTEXT_VALUES, Amount, Measure, WavesStyle, CacheContextValues, DEFAULT_CACHE_CONTEXT_VALUES, CacheContext, Cache, ML_IN_OZ, HistoryItem } from "./utils/context";
import { setPersistedDailyGoal, setPersistedMeasure, addPersistedHistoryItem, getPersistedDailyGoal, getPersistedTodayDrinks, getPersistedMeasure, removePersistedHistoryItem } from "./utils/api";

export const Root = () => {
  const [state, setState] = useState<AppContextValues>(DEFAULT_CONTEXT_VALUES);
  const [cache, setCache] = useState<CacheContextValues>(DEFAULT_CACHE_CONTEXT_VALUES);

  const recalculate = () => Promise
    .all([
      getPersistedMeasure(),
      getPersistedDailyGoal(),
      getPersistedTodayDrinks()
    ])
    .then(([configMeasure, configGoal, historyItems]) => {
      const todayAmount = sum(historyItems.map(item => {
        if (item.measure === configMeasure) {
          return item.amount
        }

        if (item.measure === Measure.ML) {
          return item.amount / ML_IN_OZ;
        }

        return item.amount * ML_IN_OZ;
      }));

      const todayProgress = todayAmount / configGoal;

      setState({
        ...DEFAULT_CONTEXT_VALUES,
        isReady: true,
        todayAmount,
        todayProgress,
        configMeasure,
        configGoal
      });
    });
    
  useEffect(() => {
    recalculate();
  }, []);

  const addDrink = (amount: Amount) => {
    const newAmount = state.todayAmount + amount;

    setState({
      ...state,
      todayAmount: newAmount,
      todayProgress: newAmount / state.configGoal,
      lastAmount: amount
    });

    addPersistedHistoryItem({
      timestamp: Date.now(),
      measure: state.configMeasure,
      amount
    });

    if ("vibrate" in window.navigator) {
      window.navigator.vibrate(150);
    }
  }

  const removeDrink = async (item: HistoryItem) => {
    await removePersistedHistoryItem(item).then(recalculate);
    window.navigator.vibrate?.(300);
  }

  const setDailyGoalAndMeasure = (configGoal: Amount, configMeasure: Measure) => {
    const todayAmount = (() => {
      if (configMeasure === state.configMeasure) {
        return state.todayAmount;
      }

      if (configMeasure === Measure.ML) {
        return state.todayAmount * ML_IN_OZ;
      }

      return state.todayAmount / ML_IN_OZ;
    })();

    setState({ ...state, todayAmount, configMeasure, configGoal });
    setPersistedDailyGoal(configGoal);
    setPersistedMeasure(configMeasure);
  }

  const setPreviousWavesStyle = (previousWavesStyle: WavesStyle) => setCache({
    ...cache,
    previousWavesStyle
  });

  const setPreviousRouteTitle = (previousRouteTitle: string) => setCache({
    ...cache,
    previousRouteTitle
  });

  const setScreenFilled = (isScreenFilled: boolean) => setCache({
    ...cache,
    isScreenFilled
  });

  const appContext: AppContext = {
    ...state,
    addDrink,
    removeDrink,
    setDailyGoalAndMeasure
  };

  const cacheContext: CacheContext = {
    ...cache,
    setPreviousRouteTitle,
    setPreviousWavesStyle,
    setScreenFilled
  }

  return (
    <Cache.Provider value={cacheContext}>
      <Context.Provider value={appContext}>
        <Switch>
          <Route
            path={RoutePath[RouteKey.INDEX]}
            exact={true}
            component={Home}
          />
          <Route
            path={RoutePath[RouteKey.ADD_DRINK]}
            exact={true}
            component={AddDrink}
          />
          <Route
            path={RoutePath[RouteKey.HISTORY]}
            exact={true}
            component={History}
          />
          <Route
            path={RoutePath[RouteKey.SET_GOAL]}
            exact={true}
            component={SetGoal}
          />
        </Switch>
      </Context.Provider>
    </Cache.Provider>
  )
};

export const App = () => (
  <BrowserRouter>
    <Root />
  </BrowserRouter>
);